import React, { useState } from "react";
import { css } from "glamor";
import { Button } from "../../components/Button";
import { Well } from "../../components/Well";
import { Byline, H6 } from "../../components/Typography";
import { blue, green, red, orange, gray30, gray40, gray60 } from "../../theme";
import { OVERRIDE_REASONS } from "../../components/bayAccess/constants/overrideReasons";

/** Bay status → dot colour, legend label + card pill. Keys match the API `BayStatus` enum (DRI-1192). */
const BAY_STATUS = {
  OPEN: {
    color: gray60,
    label: "Open (car can enter)",
    pill: "Open · car can enter",
  }, // lowered — a car can enter
  CAR_ON_TOP: {
    color: orange,
    // orange text on white is only ~1.9:1 — keep orange for the dot/border but
    // render the pill label in a readable dark grey (WCAG AA).
    textColor: gray60,
    label: "Car on top",
    pill: "Open · car on top",
  }, // lowered with a car parked over the barrier — can't be raised
  CLOSED: {
    color: green,
    label: "Closed (bay protected)",
    pill: "Closed · protected",
  }, // raised — bay protected
  JAMMED: { color: red, label: "Jammed", pill: "Jammed" },
  UNKNOWN: { color: gray40, label: "Unknown", pill: "Unknown" },
};

const statusDot = (color) =>
  css({
    display: "inline-block",
    width: 10,
    height: 10,
    borderRadius: "50%",
    backgroundColor: color,
    marginRight: 6,
    flexShrink: 0,
  });

const statusPill = (color, textColor = color) =>
  css({
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 10px",
    borderRadius: 999,
    border: `1px solid ${color}`,
    color: textColor,
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".04em",
    whiteSpace: "nowrap",
  });

const sectionLabel = { color: gray40, margin: "16px 0 6px", fontSize: 13 };

/**
 * Front-desk bollard override (DRI-1192). Presentational only — the container
 * (StreamsPage) supplies `bays` and handles `onCommand`. Open = lowered (car can
 * enter); Close = raised (bay protected). Reason is required before any action.
 */
export function BollardOverride({
  bays = [],
  onCommand,
  busy = false,
  message = null,
  loading = false,
  initialBayId = null,
  initialReason = null,
}) {
  const [selectedBayId, setSelectedBayId] = useState(initialBayId);
  const [reason, setReason] = useState(initialReason);

  const selectedBay = bays.find((b) => b.id === selectedBayId) || null;
  const canCommand = Boolean(selectedBay && reason && !busy);
  // A car on the barrier means it's already lowered and can't be raised until
  // the car leaves — both commands are no-ops the device rejects, so disable them.
  const carOnTop = Boolean(selectedBay && selectedBay.status === "CAR_ON_TOP");
  // The barrier is offline / unreadable — there's nothing to command, so hide
  // Open/Close entirely rather than offer actions that can't reach the device.
  const unknown = Boolean(selectedBay && selectedBay.status === "UNKNOWN");

  const command = (action) => {
    if (!canCommand) return;
    onCommand(selectedBay.id, action, reason);
  };

  return (
    <Well
      flavour="white"
      style={{
        flexDirection: "column",
        alignItems: "stretch",
        textAlign: "left",
        padding: "20px",
        width: "100%",
        marginTop: 0,
      }}
    >
      <Byline>Pick a bay, choose Open or Close, give a reason.</Byline>

      <div style={sectionLabel}>Select bay</div>
      {loading ? (
        <Byline>Loading bays…</Byline>
      ) : bays.length === 0 ? (
        <Byline>No Parklio bays configured for this facility.</Byline>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
          {bays.map((bay) => {
            const selected = bay.id === selectedBayId;
            const st = BAY_STATUS[bay.status];
            return (
              <button
                key={bay.id}
                onClick={() => setSelectedBayId(bay.id)}
                className={css({
                  position: "relative",
                  minWidth: 80,
                  height: 54,
                  padding: "0 18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  borderRadius: 10,
                  fontFamily: "inherit",
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#111",
                  border: `2px solid ${selected ? blue : gray30}`,
                  background: selected ? "#eef2ff" : "#fff",
                  transition: "all .15s ease",
                })}
              >
                {bay.label}
                {st && (
                  <span
                    className={css({
                      position: "absolute",
                      top: 8,
                      right: 8,
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: st.color,
                    })}
                  />
                )}
              </button>
            );
          })}
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
          margin: "10px 0 4px",
          fontSize: 12,
          color: gray40,
        }}
      >
        {Object.entries(BAY_STATUS).map(([s, { color, label }]) => (
          <span key={s} style={{ display: "flex", alignItems: "center" }}>
            <span className={statusDot(color)} />
            {label}
          </span>
        ))}
      </div>

      {selectedBay && (
        <div
          className={css({
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1rem",
            flexWrap: "wrap",
            marginTop: 14,
            padding: "16px 20px",
            borderRadius: 12,
            border: `1px solid ${gray30}`,
            background: "#fff",
          })}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 22, fontWeight: 700 }}>
              {selectedBay.label}
            </span>
            {BAY_STATUS[selectedBay.status] && (
              <span
                className={statusPill(
                  BAY_STATUS[selectedBay.status].color,
                  BAY_STATUS[selectedBay.status].textColor,
                )}
              >
                {BAY_STATUS[selectedBay.status].pill}
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
            {unknown ? (
              <span style={{ color: gray40, fontSize: 13 }}>
                The barrier's state can't be read right now — Open and Close are
                unavailable until it's back online.
              </span>
            ) : (
              <>
                <Button
                  flavour="primary"
                  size="small"
                  spaceBelow="none"
                  disabled={!canCommand || carOnTop}
                  onClick={() => command("OPEN")}
                >
                  Open
                </Button>
                <Button
                  flavour="secondary"
                  size="small"
                  mode="hollow"
                  spaceBelow="none"
                  disabled={!canCommand || carOnTop}
                  onClick={() => command("CLOSE")}
                >
                  Close
                </Button>
                {busy && (
                  <span style={{ color: gray40, fontSize: 13 }}>Sending…</span>
                )}
                {carOnTop && !busy && (
                  <span style={{ color: gray40, fontSize: 13 }}>
                    A car is on the barrier — it's already down and can't be
                    raised until the car leaves.
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <div style={sectionLabel}>Open / Close — Select Reason</div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "0.5rem",
        }}
      >
        {OVERRIDE_REASONS.map((r) => {
          const selected = r.value === reason;
          return (
            <button
              key={r.value}
              onClick={() => setReason(r.value)}
              className={css({
                height: 48,
                padding: "0 12px",
                cursor: "pointer",
                fontSize: 14,
                fontFamily: "inherit",
                fontWeight: 600,
                borderRadius: 8,
                color: "#3730a3",
                border: `2px solid ${selected ? "#4338ca" : "transparent"}`,
                background: selected ? "#e0e7ff" : "#eef2ff",
                transition: "all .15s ease",
              })}
            >
              {r.label}
            </button>
          );
        })}
      </div>

      {message && <H6 style={{ marginTop: 12, marginBottom: 0 }}>{message}</H6>}
    </Well>
  );
}
